import {useState,useEffect,useMemo} from 'react'
import {useSelector,useDispatch} from 'react-redux'

import {
	DragDropContext,
	Droppable,
	Draggable,
} from 'react-beautiful-dnd'

import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'

import {Link,useHistory} from 'react-router-dom'

import {
	useOnDemandCategories,
	useOnDemandVideos
} from './hooks'

import useS3DirectUpload from '../../hooks/useS3DirectUpload'
import useCurrentUser from '../../hooks/useCurrentUser'

import {
	sortOnDemandCategories,
	removeOnDemandVideos,
	createOnDemandVideo,
	updateCategoryEditor,
	updateVideoEditor,
	createOnDemandCategory,
	selectCategoryById,
	editOnDemandCategory,
	removeOnDemandCategory,
	selectVideoById,
	editOnDemandVideo,
	removeOnDemandVideo
} from './slice'

import YoutubeModal from '../YoutubeModal/YoutubeModal'

import {addZero} from '../../util/datetime'

import {slidingQueryComparision} from '../../util/util'

import css from './OnDemand.module.css'

const CategoryEditor = ()=>{
	const dispatch = useDispatch()

	const {show,id} = useSelector(
		state => state.onDemand.categories.editor
	)

	const category = useSelector(
		state => selectCategoryById(state,id)
	)

	const {processUpload} = useS3DirectUpload()

	const onHide = ()=>{
		dispatch(updateCategoryEditor({
			show:false,
			id:null
		}))
	}

	const [name,setName] = useState(category?.name)

	const [description,setDescription] = useState(category?.description)

	const [thumbnail,setThumbnail] = useState(category?.thumbnail)
	const [newThumbnail,setNewThumbnail] = useState('')

	useEffect(()=>{
		if(category){
			setName(category.name)
			setDescription(category.description)
			setThumbnail(category.thumbnail)
			setNewThumbnail('')
		}
	},[id])

	const [isSaving,setIsSaving] = useState(false)
	const handleSave = async ()=>{
		setIsSaving(true)
		const update = {
			name,
			description,
			thumbnail:(
				Boolean(newThumbnail)
				? (await processUpload(newThumbnail)).payload.url
				: thumbnail
			)
		}
		if(update) setIsSaving(false)
		dispatch(editOnDemandCategory({id,update}))
		onHide()
	}

	if (!category) return null

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header>
				On Demand Category Editor
			</Modal.Header>
			<Modal.Body>
				<strong>Name</strong>
				<Form.Control
					onChange={e=>{
						setName(e.target.value)
					}}
					value={name}
				/>
				<strong>Thumbnail</strong>
				{(Boolean(thumbnail)&&!Boolean(newThumbnail))&&
					<div>
						<img className="d-block w-100" src={thumbnail} />
						<Button 
							onClick={setThumbnail.bind(null,'')}
							variant="outline-danger"
							className="my-1"
							size="sm"
						>
							Remove Image
						</Button>
					</div>
				}
				{Boolean(newThumbnail)&&
					<div>
						<img className="d-block w-100" src={URL.createObjectURL(newThumbnail)} />
						<Button 
							onClick={setNewThumbnail.bind(null,'')}
							variant="outline-danger"
							className="my-1"
							size="sm"
						>
							{Boolean(thumbnail)?'Keep Original':'Remove Image'}
						</Button>
					</div>
				}
				<Form.File
					onChange={e=>{
						setNewThumbnail(e.target.files[0])
					}}
					id="category-thumbnail-select"
					label="Choose File"
					custom
				/>
				<strong>Description</strong>
				<Form.Control
					onChange={e=>{
						setDescription(e.target.value)
					}}
					value={description}
					as="textarea"
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button 
					disabled={isSaving}
					onClick={handleSave}
					variant="outline-success"
					size="sm"
				>
					Save {isSaving&&<Spinner animation="border" size="sm"/>}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

const SortModal = ({show,onHide})=>{
	const dispatch = useDispatch()
	const {status,categories} = useOnDemandCategories()
	const handleDragEnd = result => {
		if (categories.length <= 1 || !Boolean(result.destination)) return
		const { source: { index: srcIdx }, destination: { index: tgtIdx } } = result
		//get a temp array with the src item removed
		const tmpNoSrc = [
			...categories.slice(0, srcIdx),
			...categories.slice(srcIdx + 1)
		]
		
		//perform deep copy "hack" so we can mutate the update object
		//with the new routine orders
		const categoriesUpdate = JSON.parse(JSON.stringify([
			...tmpNoSrc.slice(0, tgtIdx),
			categories[srcIdx],
			...tmpNoSrc.slice(tgtIdx)
		])).map((c,i)=>(
			{
				...c,
				sort_order:i
			}
		))

		const new_order = categoriesUpdate.map(({id})=>id)

		dispatch(sortOnDemandCategories({
			categories:categoriesUpdate,
			new_order
		}))
	}
	return (
		<Modal 
			onHide={onHide}
			show={show} 
		>
			<Modal.Header>
				Sort On Demand Categories
			</Modal.Header>
			<Modal.Body>
				{status==='pending'&&
				<div className="text-center p-5">
					<Spinner animation="border" />
				</div>
			}

			{(status==='idle'&&categories.length>0)&&
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="category-list" type="ON_DEMAND_CATEGORIES">
						{(provided, snapshot) => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
							>
								{categories.sort((a,b)=>a.sort_order - b.sort_order).map((category,idx)=>(
									<Draggable
										key={category.id}
										draggableId={`category-${category.id}`}
										index={idx}
									>
										{(provided, snapshot) => (
											<div
												className="pb-1" 
												ref={provided.innerRef} 
												{...provided.draggableProps}
											>
												<OverlayTrigger overlay={
													<Tooltip>Drag & re-order</Tooltip>
												}>
													<i 
														ref={provided.innerRef}
														{...provided.dragHandleProps}
														className="fas fa-grip-vertical text-muted mr-2" 
													/>
												</OverlayTrigger>
												{category.name}
											</div>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			}
			</Modal.Body>
		</Modal>
	)
}

const Categories = ()=>{
	const dispatch = useDispatch()
	const user = useCurrentUser()
	const history = useHistory()
	const isClient = user.account_type === 'client'
	const isOwner = (
		parseInt(user.gym_id) !== 0
		? user.account_type === 'gym'
		: user.account_type === 'trainer'
	)
	const {status,categories} = useOnDemandCategories()

	const [isAdding,setIsAdding] = useState(false)
	const [showSortModal,setShowSortModal] = useState(false)

	const handleEdit = (id)=>{
		dispatch(updateCategoryEditor({show:true,id}))
	}

	const handleRemove = (id)=>{
		const confirmation = 'Are you sure you want to delete this category?'
		if (!window.confirm(confirmation)) return
		dispatch(removeOnDemandCategory({id}))
	}

	const handleAdd = ()=>{
		setIsAdding(true)
		dispatch(createOnDemandCategory({})).then(({payload})=>{
			setIsAdding(false)
			handleEdit(payload.data.on_demand_category.id)
		})
	}
	return (
		<div className="w-100">
			{isOwner&&
				<div className="text-right">
					<Button 
						variant="outline-dark"
						onClick={setShowSortModal.bind(null,true)}
						className="mr-1"
						size="sm"
					>
						Sort Categories
					</Button>
					<Button 
						variant="outline-primary"
						disabled={isAdding}
						onClick={handleAdd}
						size="sm"
					>
						Add Category {isAdding&&<Spinner animation="border" size="sm"/>}
					</Button>
				</div>
			}
			{status==='pending'&&
				<div className="text-center p-5">
					<Spinner animation="border" />
				</div>
			}
			{(status==='idle'&&categories.length>0)&&
				<div className={css.Grid}>
					{categories.sort((a,b)=>a.sort_order - b.sort_order).map(({name,id,description,thumbnail})=>(
					<div 
						style={{
							cursor:isClient?'pointer':'default',
						}}
						key={id} 
						onClick={()=>{
							if(isOwner) return 
							history.push(`/on-demand?category=${id}`)
						}}
						className="d-flex rounded border mt-2 overflow-hidden">
						{Boolean(thumbnail)&&
							<Link to={`/on-demand?category=${id}`}>
								<img 
									src={thumbnail} 
									style={{
										width:'80px',
										height:'80px',
										objectFit:'cover',
										cursor:'pointer',
										flex:'0 0 auto'
									}}
								/>
							</Link>
						}
						<div className="d-flex p-2 justify-content-between">
							<div>
								<div>
									<Link to={`/on-demand?category=${id}`}>
										{Boolean(name)?name:'Untitled Category'}
									</Link>
								</div>
								<small>{description}</small>
							</div>
							{isOwner&&
								<div>
									<Button
										onClick={handleEdit.bind(null,id)}
										variant="outline-primary"
										className="ml-1 mb-1"
										size="sm"
									>
										<i className="fa fa-edit"/>
									</Button>
									<Button
										onClick={handleRemove.bind(null,id)}
										variant="outline-danger"
										className="ml-1 mb-1"
										size="sm"
									>
										<i className="fa fa-trash"/>
									</Button>
								</div>
							}
						</div>
					</div>
				))}
				</div>
			}
			<SortModal
				onHide={setShowSortModal.bind(null,false)}
				show={showSortModal}
			/>
			{(status==='idle'&&categories.length===0)&&
				'No categories added yet.'
			}
			<CategoryEditor/>
		</div>
	)
}

const VideoEditor = ()=>{
	const dispatch = useDispatch()

	const {show,id} = useSelector(
		state => state.onDemand.videos.editor
	)

	const video = useSelector(
		state => selectVideoById(state,id)
	)

	const {status,categories} = useOnDemandCategories()

	const onHide = ()=>{
		dispatch(
			updateVideoEditor({
				show:false,
				id:null
			})
		)
	}

	const [name,setName] = useState(video?.name)
	const [description,setDescription] = useState(video?.description)
	const [url,setUrl] = useState(video?.url)
	const [time,setTime] = useState(video?.time)
	const [hours,setHours] = useState()
	const [minutes,setMinutes] = useState()
	const [seconds,setSeconds] = useState()
	const [category,setCategory] = useState()

	useEffect(()=>{
		if (video) {
			setName(video.name)
			setDescription(video.description)
			setUrl(video.url)
			setTime(video.time)
			setHours(video.time.split(':')[0])
			setMinutes(video.time.split(':')[1])
			setSeconds(video.time.split(':')[2])
			setCategory(video.category_id)
		}
	},[id])

	const handleSave = ()=>{
		dispatch(
			editOnDemandVideo({
				id,update:{name,description,url,time:`00:${minutes}:${seconds}`,category_id:category}
			})
		)
		onHide()
	}

	if (!video) return null

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header>
				On Demand Video Editor
			</Modal.Header>
			<Modal.Body>
				<strong>Name</strong>
				<Form.Control
					onChange={e=>{
						setName(e.target.value)
					}}
					value={name}
				/>
				<strong>Description</strong>
				<Form.Control
					onChange={e=>{
						setDescription(e.target.value)
					}}
					value={description}
					as="textarea"
				/>
				<strong>Link/URL</strong>
				<Form.Control
					onChange={e=>{
						setUrl(e.target.value)
					}}
					value={url}
				/>
				<strong>Time</strong>
				<div className="d-flex align-items-center">
					<div>
						<small className="d-block">Minutes</small>
						<Form.Control
							onChange={e=>{
								setMinutes(e.target.value)
							}}
							value={minutes}
						/>
					</div>
					<span className="mx-1"/>
					<div>
						<small className="d-block">Seconds</small>
						<Form.Control
							onChange={e=>{
								setSeconds(e.target.value)
							}}
							value={seconds}
						/>
					</div>
				</div>
				<strong>Category</strong>
				<Form.Control 
					value={category}
					onChange={e=>{
						setCategory(e.target.value)
					}}
					as="select"
				>
					{categories.map(({id,name})=>(
						<option value={id}>{name}</option>
					))}
				</Form.Control>
			</Modal.Body>
			<Modal.Footer>
				<Button 
					onClick={handleSave}
					variant="outline-success"
					size="sm"
				>
					Save
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

const Videos = ({categoryId})=>{
	const dispatch = useDispatch()
	const history = useHistory()
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const isOwner = (
		parseInt(user.gym_id) !== 0
		? user.account_type === 'gym'
		: user.account_type === 'trainer'
	)
	const category = useSelector(
		state => selectCategoryById(state,categoryId)
	)

	const {status,videos} = useOnDemandVideos(categoryId)

	const [enableBulkDelete,setEnableBulkDelete] = useState(false)
	const [toDelete,setToDelete] = useState([])

	const toggleDeleteItem = id => {
		setToDelete(
			p => (
				p.includes( id ) 
				? p.filter( i => i !== id ) 
				: [ ...p , id ] 
			)
		)
	}

	const [timeFilter,setTimeFilter] = useState('any')

	const [query,setQuery] = useState('')

	const filterMin = ()=>{
		if (timeFilter==='any') return '00:00:00'
		return `00:${addZero(parseInt(timeFilter))}:00`
	}

	const filterMax = ()=>{
		if (timeFilter==='any'||parseInt(timeFilter)===30) return '99:99:99'
		return `00:${addZero(parseInt(timeFilter)+15)}:00`
	}

	const filteredVides = useMemo(
		()=>videos.filter( v =>{
			const min = filterMin()
			const max = filterMax()
			return min <= v.time && v.time <= max
		}),
		[videos,timeFilter]
	)

	const sortedVideos = useMemo(
		()=>filteredVides.sort((a,b)=>slidingQueryComparision(a.name,b.name,query)),
		[filteredVides,query]
	)

	const timeOptions = [
		{
			label:'Any Length',
			value:'any'
		},{
			label:'0-15min',
			value:0
		},{
			label:'15-30min',
			value:15
		},{
			label:'30+',
			value:30
		}
	]

	

	//if page is reloaded ensure that categories are loaded
	useOnDemandCategories()

	const [isAdding,setIsAdding] = useState(false)

	const handleEdit = (id)=>{
		dispatch(updateVideoEditor({show:true,id}))
	}

	const handleRemove = (id)=>{
		const confirmation = 'Are you sure you want to delete this video?'
		if (!window.confirm(confirmation)) return
		dispatch(removeOnDemandVideo({id}))
	}

	const handleAdd = ()=>{
		setIsAdding(true)
		dispatch(createOnDemandVideo({category_id:categoryId}))
			.then(({payload})=>{
				setIsAdding(false)
				handleEdit(payload.data.on_demand_video.id)
			})
	}

	const handleBulkDelete = ()=>{
		if(!enableBulkDelete){
			setEnableBulkDelete(true)
		}else {
			if(!window.confirm('Are you sure you want to delete these videos?')) return
			dispatch(removeOnDemandVideos({ids:toDelete}))
			setEnableBulkDelete(false)
			setToDelete([])

		}
	}

	const [showVideoModal,setShowVideoModal] = useState(false)
	const [videoModalUrl,setVideoModalUrl] = useState('')
	const [videoModalDescription,setVideoModalDescription] = useState('')
	const [videoModalTitle,setVideoModalTitle] = useState('')

	return (
		<div>
			<div className="d-flex justify-content-between">
				<div>
					<Link to="/on-demand">All Categories</Link>
					<span className="mx-1">/</span>
					{category?.name}
				</div>
				{isOwner&&
					<div>
						<ButtonGroup size="sm">
							{enableBulkDelete&&
								<Button 
									onClick={()=>{
										setEnableBulkDelete(false)
										setToDelete([])
									}}
									variant="outline-secondary"
								>
									Cancel
								</Button>
							}
							<OverlayTrigger overlay={
								<Tooltip>
									{
										enableBulkDelete
										? 'Click the check box to select a video'
										: 'Select and delete multiple vidoes'
									}
								</Tooltip>
							}>
								<Button 
									variant={
										enableBulkDelete
										? "danger"
										: "outline-danger"
									}
									onClick={handleBulkDelete}
									className="mr-1"
									size="sm"
								>
									{enableBulkDelete?'Delete':'Bulk Delete'}
								</Button>
							</OverlayTrigger>
						</ButtonGroup>
						<Button 
							variant="outline-primary"
							disabled={isAdding}
							onClick={handleAdd}
							size="sm"
						>
							Add Video {isAdding&&<Spinner animation="border" size="sm"/>}
						</Button>
					</div>
				}
			</div>
			<div className="d-flex align-items-end">
				<Form.Control 
					onChange={e=>{
						setQuery(e.target.value)
					}}
					value={query}
					placeholder="search" 
					className="mt-2"
				/>
				<Form.Control 
					onChange={e=>{
						setTimeFilter(e.target.value)
					}}
					value={timeFilter}
					className="ml-1"
					style={{
						width:'140px'
					}}
					as="select"
				>
					{timeOptions.map(({label,value})=>(
						<option value={value}>{label}</option>
					))}
				</Form.Control>
			</div>
			{status==='pending'&&
				<div className="text-center p-5">
					<Spinner animation="border" />
				</div>
			}
			{(status==='idle'&&videos.length>0)&&
				<div className={css.Grid}>
					{sortedVideos.map(({name,description,url,time,id,youtube_thumbnail})=>(
						<div 
							style={{
								cursor:isClient?'pointer':'default'
							}}
							onClick={()=>{
								if (isOwner) return
								setVideoModalUrl(url)
								setVideoModalTitle(name)
								setVideoModalDescription(description)
								setShowVideoModal(true)
							}}
							key={id} 
							className="mt-2 rounded border d-flex overflow-hidden"
						>
							{Boolean(youtube_thumbnail)&&
								<div 
									className="position-relative" 
									onClick={()=>{
										setVideoModalUrl(url)
										setVideoModalTitle(name)
										setVideoModalDescription(description)
										setShowVideoModal(true)
									}}
								>
									<img 
										src={youtube_thumbnail}
										style={{
											cursor:'pointer',
											width:'80px',
											height:'80px',
											objectFit:'cover',
											display:'block',
											marginRight:'8px',
											flex:'0 0 auto'
										}}
									/>
									<i 
										className="fa fa-play text-white bg-dark rounded p-1"
										style={{
											transform:'translate(-50%,-50%)',
											position:'absolute',
											top:'50%',
											left:'50%',
											cursor:'pointer'
										}}
									/>
								</div>
							}
							<div className="w-100 p-2">
								<div className="d-flex justify-content-between">
									<div
										style={{cursor:'pointer'}}
										onClick={()=>{
											setVideoModalUrl(url)
											setVideoModalDescription(description)
											setShowVideoModal(true)
											setVideoModalTitle(name)
										}}
										className="text-primary"
										variant="link"
									>
										{Boolean(name)?name:'Untitled Video'} ({time.split(':').slice(1).map(addZero).join(':')})
									</div>
									{isOwner&&
										<div className="d-flex">
											{
												enableBulkDelete ?
												<Form.Check
													onChange={toggleDeleteItem.bind(null,id)}
													className="text-danger"
													id={`video-check-${id}`}
													label="delete"
												/>
												:
												<>
													<Button 
														onClick={handleEdit.bind(null,id)}
														variant="outline-primary"
														className="ml-1 mb-1"
														size="sm"
													>
														<i className="fa fa-edit"/>
													</Button>
													<Button 
														onClick={handleRemove.bind(null,id)}
														variant="outline-danger"
														className="ml-1 mb-1"
														size="sm"
													>
														<i className="fa fa-trash"/>
													</Button>
												</>
											}
										</div>
									}
								</div>
								<div>
									{description}
								</div>
							</div>
						</div>
					))}
				</div>
			}
			{(status==='idle'&&videos.length===0)&&
				'No videos added yet.'
			}
			<VideoEditor />
			<YoutubeModal
				title={videoModalTitle}
				description={videoModalDescription}
				waiver="By watching and following along with this fitness/health video, you acknowledge that you are voluntarily participating in physical activity, which may include strenuous exercise and movement. You understand and acknowledge that there are risks associated with physical activity and exercise, including the risk of serious injury or death. You further acknowledge that you have consulted with your personal trainer or healthcare provider before participating in this video and have received clearance to do so. You hereby release and waive any claims against the video provider for any injuries or damages that may occur as a result of your participation in the video. By continuing to watch and follow along with the video, you agree to the terms of this waiver."
				demoVideo={videoModalUrl} 
				demoVideoShow={showVideoModal} 
				setDemoVideoShow={setShowVideoModal}
				playing={false}
				loop={false}
			/>
		</div>
	)
}

const OnDemand = ({location})=>{
	
	const params = new URLSearchParams(location.search)
	const categoryId = params.get('category')

	return (
		<div className="pb-5 pt-2 px-2 px-md-3">
			<strong>On Demand</strong>
			<p>Fitness and health video library</p>
			{
				Boolean(categoryId)
				? <Videos categoryId={categoryId} />
				: <Categories/>
			}
		</div>
	)
}

export default OnDemand